import React from 'react';
import {LineChart} from '@mui/x-charts/LineChart';
import {OrderStatistic} from "../../models/order";
import {orderStatisticApi} from "./orderApi";
import {NotificationService} from "../../common/notification";
import CircularProgress from "@mui/joy/CircularProgress";
import moment from "moment";
import { formatWith } from "../../common/dateTime";
import Sheet from "@mui/joy/Sheet";
import {DemoContainer} from '@mui/x-date-pickers/internals/demo';
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import {DatePicker} from '@mui/x-date-pickers/DatePicker';
import IconButton from "@mui/joy/IconButton";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import Modal from "@mui/joy/Modal";
import ModalDialog from "@mui/joy/ModalDialog";
import ModalClose from "@mui/joy/ModalClose";
import Typography from "@mui/joy/Typography";
import Divider from "@mui/joy/Divider";
import Button from "@mui/joy/Button";
import Box from "@mui/joy/Box";
import FormControl from "@mui/joy/FormControl";
import FormLabel from "@mui/joy/FormLabel";
import Select from "@mui/joy/Select";
import Option from "@mui/joy/Option";
import dayjs from 'dayjs';
import {DefaultizedPieValueType} from '@mui/x-charts/models';
import {PieChart, pieArcLabelClasses} from '@mui/x-charts/PieChart';
import {kopecksToRublesNumber} from "../../common/money";

export function OrdersCharts() {
    const [statistics, setStatistics] = React.useState<OrderStatistic[]>([]);
    const [orderStatistic, setOrderStatistic] = React.useState<any[]>([]);
    const [isLoading, setLoading] = React.useState(false);
    const [from, setFrom] = React.useState(dayjs(new Date(new Date().setMonth(new Date().getMonth() - 1))));
    const [to, setTo] = React.useState(dayjs(new Date()));
    const [groupType, setGroupType] = React.useState(0);
    const [open, setOpen] = React.useState(false);

    React.useEffect(() => {
        loadData().catch(console.error);
    }, []);

    React.useEffect(() => {
        loadData().catch(console.error);
    }, [from, to]);

    const loadData = async () => {
        if (from > to) {
            return;
        }
        setLoading(true)
        try {
            let result = await orderStatisticApi(from.toDate(), to.toDate());
            if (result.result) {
                setStatistics(result.value!)
            } else {
                NotificationService.alertError("Пожалуйста попробуйте позже или обратитесь в службу поддержки")
            }

        } finally {
            setLoading(false)
        }
    }

    React.useEffect(() => {
        const res: OrderStatistic [] = [];

        for (let i = statistics.length - 1; i >= 0; i--) {
            const item = statistics[i];
            let date: string;
            date = formatWith(item.hour as any, 'DD.MM.YYYY');
            if (groupType === 1)
                date = formatWith(item.hour as any, 'MM.YYYY');
            if (groupType === 2)
                date = formatWith(item.hour as any, 'YYYY');
            if (groupType === 3)
                date = formatWith(item.hour as any, 'DD.MM.YYYY HH');
            const prevValue = res.filter(_ => _.hour === date);
            if (prevValue.length === 1) {
                prevValue[0].totalCount += item.totalCount;
                prevValue[0].totalSum += kopecksToRublesNumber(item.totalSum );
                prevValue[0].csCount += item.csCount;
                prevValue[0].csSum += kopecksToRublesNumber(item.csSum);
                prevValue[0].dota2Count += item.dota2Count;
                prevValue[0].dota2Sum += kopecksToRublesNumber(item.dota2Sum);
                prevValue[0].tf2Count += item.tf2Count;
                prevValue[0].tf2Sum += kopecksToRublesNumber(item.tf2Sum );
                prevValue[0].giftCount += item.giftCount;
                prevValue[0].giftSum += kopecksToRublesNumber(item.giftSum);
                prevValue[0].rustCount += item.rustCount;
                prevValue[0].rustSum += kopecksToRublesNumber(item.rustSum);
                prevValue[0].externalCount += item.externalCount ?? 0;
                prevValue[0].externalSum += kopecksToRublesNumber(item.externalSum ?? 0);
            } else {
                res.push({
                    ...item,
                    hour: date,
                    date: moment(date, getFormat()).toDate(),
                    totalSum: kopecksToRublesNumber(item.totalSum),
                    csSum: kopecksToRublesNumber(item.csSum),
                    dota2Sum: kopecksToRublesNumber(item.dota2Sum),
                    tf2Sum: kopecksToRublesNumber(item.tf2Sum),
                    giftSum: kopecksToRublesNumber(item.giftSum),
                    rustSum: kopecksToRublesNumber(item.rustSum),
                    externalCount: item.externalCount ?? 0,
                    externalSum: kopecksToRublesNumber(item.externalSum ?? 0),
                });
            }
        }
        setOrderStatistic(res.sort((a, b) => a.date > b.date ? 1 : -1))
    }, [statistics, groupType]);

    const getFormat = () => {
        if (groupType === 1)
            return 'MM.YYYY';
        if (groupType === 2)
            return 'YYYY';
        if (groupType === 3)
            return 'DD.MM.YYYY HH';
        return 'DD.MM.YYYY';
    }

    const renderFilters = () => {
        return (
            <React.Fragment>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DemoContainer components={['FromDatePicker']}>
                        <DatePicker label="С"
                                    value={from}
                                    onChange={(newValue) => setFrom(newValue !== null ? newValue : dayjs(new Date(new Date().setMonth(new Date().getMonth() - 1))))}/>
                    </DemoContainer>
                </LocalizationProvider>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DemoContainer components={['ToDatePicker']}>
                        <DatePicker label="По"
                                    value={to}
                                    onChange={(newValue) => setTo(newValue !== null ? newValue : dayjs(new Date()))}/>
                    </DemoContainer>
                </LocalizationProvider>
                <FormControl size="sm">
                    <FormLabel>Групировать по</FormLabel>
                    <Select
                        size="sm"
                        placeholder="Групировать по"
                        slotProps={{button: {sx: {whiteSpace: 'nowrap'}}}}
                        value={groupType}
                        onChange={(_, value) => setGroupType(value as 0 | 1 | 2 | 3)}
                    >
                        <Option value={0}>День</Option>
                        <Option value={1}>Месяц</Option>
                        <Option value={2}>Год</Option>
                        <Option value={3}>Час</Option>
                    </Select>
                </FormControl>
            </React.Fragment>
        );
    }

    const getArcTotalLabel = (params: DefaultizedPieValueType) => {
        const totalCount = orderStatistic.map((item) => item.totalCount).reduce((a, b) => a + b, 0);
        const percent = params.value / totalCount;
        return `${(percent * 100).toFixed(2)}%`;
    };

    const getArcSumLabel = (params: DefaultizedPieValueType) => {
        const totalSum = orderStatistic.map((item) => item.totalSum).reduce((a, b) => a + b, 0);
        const percent = params.value / totalSum;
        return `${(percent * 100).toFixed(2)}%`;
    };

    const getDataForSumPie = () => {
        const csSum = orderStatistic.map((item) => item.csSum).reduce((a, b) => a + b, 0)
        const dota2Sum = orderStatistic.map((item) => item.dota2Sum).reduce((a, b) => a + b, 0)
        const tf2Sum = orderStatistic.map((item) => item.tf2Sum).reduce((a, b) => a + b, 0)
        const giftSum = orderStatistic.map((item) => item.giftSum).reduce((a, b) => a + b, 0)
        const rustSum = orderStatistic.map((item) => item.rustSum).reduce((a, b) => a + b, 0)
        return [
            {label: 'CS', value: csSum, color: '#0088FE'},
            {label: 'Dota 2', value: dota2Sum, color: '#00C49F'},
            {label: 'TF 2', value: tf2Sum, color: '#FFBB28'},
            {label: 'GIFT', value: giftSum, color: '#FF8042'},
            {label: 'Rust', value: rustSum, color: '#8884d8'}
        ];
    }

    /** Живые продавцы и поставщики: доли по сумме и по количеству */
    const getDataForSourcePie = (field: 'sum' | 'count') => {
        const total = orderStatistic.map((item) => field === 'sum' ? item.totalSum : item.totalCount).reduce((a, b) => a + b, 0);
        const external = orderStatistic.map((item) => field === 'sum' ? item.externalSum : item.externalCount).reduce((a, b) => a + b, 0);
        return [
            {label: 'Живые продавцы', value: Math.max(0, total - external), color: '#90caf9'},
            {label: 'Поставщики', value: external, color: '#1976d2'}
        ];
    }

    const getArcSourceLabel = (field: 'sum' | 'count') => (params: DefaultizedPieValueType) => {
        const total = orderStatistic.map((item) => field === 'sum' ? item.totalSum : item.totalCount).reduce((a, b) => a + b, 0);
        return total > 0 ? `${(params.value / total * 100).toFixed(2)}%` : '';
    };

    const getDataForTotalPie = () => {
        const cs = orderStatistic.map((item) => item.csCount).reduce((a, b) => a + b, 0)
        const dota2 = orderStatistic.map((item) => item.dota2Count).reduce((a, b) => a + b, 0)
        const tf2 = orderStatistic.map((item) => item.tf2Count).reduce((a, b) => a + b, 0)
        const gift = orderStatistic.map((item) => item.giftCount).reduce((a, b) => a + b, 0)
        const rust = orderStatistic.map((item) => item.rustCount).reduce((a, b) => a + b, 0)
        return [
            {label: 'CS', value: cs, color: '#0088FE'},
            {label: 'Dota 2', value: dota2, color: '#00C49F'},
            {label: 'TF 2', value: tf2, color: '#FFBB28'},
            {label: 'GIFT', value: gift, color: '#FF8042'},
            {label: 'Rust', value: rust, color: '#8884d8'}
        ];
    }

    return (
        isLoading ?
            <CircularProgress/> :
            <>
                <Sheet
                    className="SearchAndFilters-mobile"
                    sx={{display: {xs: 'flex', sm: 'none'}, my: 1, gap: 1}}
                >
                    <IconButton
                        size="sm"
                        variant="outlined"
                        color="neutral"
                        onClick={() => setOpen(true)}
                    >
                        <FilterAltIcon/>
                    </IconButton>

                    <Modal open={open} onClose={() => setOpen(false)}>
                        <ModalDialog aria-labelledby="filter-modal" layout="fullscreen">
                            <ModalClose/>
                            <Typography id="filter-modal" level="h2">
                                Фильтры
                            </Typography>
                            <Divider sx={{my: 2}}/>
                            <Sheet sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
                                {renderFilters()}
                                <Button color="neutral" onClick={() => setOpen(false)}>
                                    Закрыть
                                </Button>
                            </Sheet>
                        </ModalDialog>
                    </Modal>
                </Sheet>
                <Box
                    className="SearchAndFilters-tabletUp"
                    sx={{
                        borderRadius: 'sm',
                        py: 2,
                        display: {xs: 'none', sm: 'flex'},
                        flexWrap: 'wrap',
                        gap: 1.5,
                        '& > *': {
                            minWidth: {xs: '120px', md: '160px'},
                        },
                    }}
                >
                    {renderFilters()}
                </Box>

                <LineChart
                    dataset={orderStatistic}
                    xAxis={[
                        {
                            dataKey: 'date',
                            scaleType: 'band',
                            valueFormatter: (date) => formatWith(date as any, getFormat()),
                        },
                    ]}
                    series={[
                        {
                            dataKey: 'totalSum',
                            label: 'Сумма ордеров',
                        }
                    ]}
                    height={300}
                />

                <LineChart
                    dataset={orderStatistic}
                    xAxis={[
                        {
                            dataKey: 'date',
                            scaleType: 'band',
                            valueFormatter: (date) => formatWith(date as any, getFormat()),
                        },
                    ]}
                    series={[
                        {
                            dataKey: 'totalCount',
                            label: 'Всего ордеров',
                        }
                    ]}
                    height={300}
                />

                <LineChart
                    dataset={orderStatistic}
                    xAxis={[
                        {
                            dataKey: 'date',
                            scaleType: 'band',
                            valueFormatter: (date) => formatWith(date as any, getFormat()),
                        },
                    ]}
                    series={[
                        {
                            dataKey: 'totalSum',
                            label: 'Сумма всех ордеров',
                            color: '#90caf9',
                        },
                        {
                            dataKey: 'externalSum',
                            label: 'Сумма через поставщиков',
                            color: '#1976d2',
                        }
                    ]}
                    height={300}
                />

                <LineChart
                    dataset={orderStatistic}
                    xAxis={[
                        {
                            dataKey: 'date',
                            scaleType: 'band',
                            valueFormatter: (date) => formatWith(date as any, getFormat()),
                        },
                    ]}
                    series={[
                        {
                            dataKey: 'externalCount',
                            label: 'Предметов через поставщиков',
                            color: '#1976d2',
                        }
                    ]}
                    height={300}
                />

                <LineChart
                    dataset={orderStatistic}
                    xAxis={[
                        {
                            dataKey: 'date',
                            scaleType: 'band',
                            valueFormatter: (date) => formatWith(date as any, getFormat()),
                        },
                    ]}
                    series={[
                        {
                            dataKey: 'csSum',
                            label: 'Сумма CS2 ордеров',
                        }
                    ]}
                    height={300}
                />

                <LineChart
                    dataset={orderStatistic}
                    xAxis={[
                        {
                            dataKey: 'date',
                            scaleType: 'band',
                            valueFormatter: (date) => formatWith(date as any, getFormat()),
                        },
                    ]}
                    series={[
                        {
                            dataKey: 'csCount',
                            label: 'Всего CS ордеров',
                        }
                    ]}
                    height={300}
                />

                <LineChart
                    dataset={orderStatistic}
                    xAxis={[
                        {
                            dataKey: 'date',
                            scaleType: 'band',
                            valueFormatter: (date) => formatWith(date as any, getFormat()),
                        },
                    ]}
                    series={[
                        {
                            dataKey: 'dota2Sum',
                            label: 'Сумма Dota 2 ордеров',
                        }
                    ]}
                    height={300}
                />

                <LineChart
                    dataset={orderStatistic}
                    xAxis={[
                        {
                            dataKey: 'date',
                            scaleType: 'band',
                            valueFormatter: (date) => formatWith(date as any, getFormat()),
                        },
                    ]}
                    series={[
                        {
                            dataKey: 'dota2Count',
                            label: 'Всего Dota 2 ордеров',
                        }
                    ]}
                    height={300}
                />

                <LineChart
                    dataset={orderStatistic}
                    xAxis={[
                        {
                            dataKey: 'date',
                            scaleType: 'band',
                            valueFormatter: (date) => formatWith(date as any, getFormat()),
                        },
                    ]}
                    series={[
                        {
                            dataKey: 'tf2Sum',
                            label: 'Сумма TF 2 ордеров',
                        }
                    ]}
                    height={300}
                />

                <LineChart
                    dataset={orderStatistic}
                    xAxis={[
                        {
                            dataKey: 'date',
                            scaleType: 'band',
                            valueFormatter: (date) => formatWith(date as any, getFormat()),
                        },
                    ]}
                    series={[
                        {
                            dataKey: 'tf2Count',
                            label: 'Всего TF 2 ордеров',
                        }
                    ]}
                    height={300}
                />

                <LineChart
                    dataset={orderStatistic}
                    xAxis={[
                        {
                            dataKey: 'date',
                            scaleType: 'band',
                            valueFormatter: (date) => formatWith(date, getFormat()),
                        },
                    ]}
                    series={[
                        {
                            dataKey: 'giftSum',
                            label: 'Сумма GIFT ордеров',
                        }
                    ]}
                    height={300}
                />

                <LineChart
                    dataset={orderStatistic}
                    xAxis={[
                        {
                            dataKey: 'date',
                            scaleType: 'band',
                            valueFormatter: (date) => formatWith(date, getFormat()),
                        },
                    ]}
                    series={[
                        {
                            dataKey: 'giftCount',
                            label: 'Всего GIFT ордеров',
                        }
                    ]}
                    height={300}
                />

                <LineChart
                    dataset={orderStatistic}
                    xAxis={[
                        {
                            dataKey: 'date',
                            scaleType: 'band',
                            valueFormatter: (date) => formatWith(date, getFormat()),
                        },
                    ]}
                    series={[
                        {
                            dataKey: 'rustSum',
                            label: 'Сумма Rust ордеров',
                        }
                    ]}
                    height={300}
                />

                <LineChart
                    dataset={orderStatistic}
                    xAxis={[
                        {
                            dataKey: 'date',
                            scaleType: 'band',
                            valueFormatter: (date) => formatWith(date, getFormat()),
                        },
                    ]}
                    series={[
                        {
                            dataKey: 'rustCount',
                            label: 'Всего Rust ордеров',
                        }
                    ]}
                    height={300}
                />

                <Box flexGrow={1}>
                    <Typography sx={{justifySelf:'center'}}>Сумма</Typography>
                    <PieChart
                        series={[
                            {
                                outerRadius: 80,
                                data: getDataForSumPie(),
                                arcLabel: getArcSumLabel,
                            },
                        ]}
                        sx={{
                            [`& .${pieArcLabelClasses.root}`]: {
                                fill: 'white',
                                fontSize: 14,
                            },
                        }}
                        height={400}
                    />
                </Box>
                <Box flexGrow={1}>
                    <Typography sx={{justifySelf:'center'}}>Источник: сумма</Typography>
                    <PieChart
                        series={[
                            {
                                outerRadius: 80,
                                data: getDataForSourcePie('sum'),
                                arcLabel: getArcSourceLabel('sum'),
                            },
                        ]}
                        sx={{
                            [`& .${pieArcLabelClasses.root}`]: {
                                fill: 'white',
                                fontSize: 14,
                            },
                        }}
                        height={400}
                    />
                </Box>
                <Box flexGrow={1}>
                    <Typography sx={{justifySelf:'center'}}>Источник: кол-во</Typography>
                    <PieChart
                        series={[
                            {
                                outerRadius: 80,
                                data: getDataForSourcePie('count'),
                                arcLabel: getArcSourceLabel('count'),
                            },
                        ]}
                        sx={{
                            [`& .${pieArcLabelClasses.root}`]: {
                                fill: 'white',
                                fontSize: 14,
                            },
                        }}
                        height={400}
                    />
                </Box>
                <Box flexGrow={1}>
                    <Typography sx={{justifySelf:'center'}}>Кол-во</Typography>
                    <PieChart
                        series={[
                            {
                                outerRadius: 80,
                                data: getDataForTotalPie(),
                                arcLabel: getArcTotalLabel,
                            },
                        ]}
                        sx={{
                            [`& .${pieArcLabelClasses.root}`]: {
                                fill: 'white',
                                fontSize: 14,
                            },
                        }}
                        height={400}
                    />
                </Box>
            </>
    );
}